import { AuthViewProps } from "@homebound/auth-components";
import { AnimatePresenceProps, motion } from "framer-motion";
import { ReactNode, useEffect } from "react";
import { Route, RouteProps, Routes, useNavigate } from "react-router-dom";
import { unauthorizedUserPath } from "src/RouteUrls";

// Key for storing redirect URL in localStorage (for magic link flow)
export const REDIRECT_KEY = "authRedirectUrl";

type ChildRouteProps = RouteProps & { requireAuth: boolean };
type AuthProps = AuthViewProps & { isAuthenticating: boolean; checkoutEnabledHomeowner: boolean };
type AnimatedRoutesProps = AnimatePresenceProps & { children: ChildRouteProps[]; authProps: AuthProps };

/** Animates in and out routes */
export function AnimatedRoutes(props: AnimatedRoutesProps) {
  const { children, authProps } = props;
  return (
    <Routes>
      {children.map(({ path, element, requireAuth = true, ...otherRouteProps }) => (
        <Route
          key={path}
          path={path}
          element={
            // element
            <AuthWrapper requireAuth={requireAuth} authProps={authProps}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.2 } }}
                exit={{ opacity: 0, transition: { duration: 0.2 } }}
              >
                {element}
              </motion.div>
            </AuthWrapper>
          }
          {...otherRouteProps}
        />
      ))}
    </Routes>
  );
}

/**
 * Normally we would use `AuthenticatedRoute` and `UnauthenticatedRoute` from `"@homebound/auth-components`,
 * however that package assumes react-router v5, and this repo is using v6 which is not compatible (dropped
 * support for render functions).
 */
function AuthWrapper(props: { children: ReactNode; requireAuth: boolean; authProps: AuthProps }) {
  const { children, requireAuth, authProps } = props;
  const navigate = useNavigate();

  const { isAuthenticating, isAuthenticated, signInPath, checkoutEnabledHomeowner } = authProps;

  // Redirect based on auth status, in the future if we need unauthenticated pages that don't redirect we can revisit
  useEffect(() => {
    // If we're currently checking the auth status, wait until that resolves before redirecting
    if (isAuthenticating) return;

    if (requireAuth && !isAuthenticated) {
      // Preserve current URL for post-auth navigation (unless it's the unauthorized page)
      // Store in both query param (for manual code entry) and localStorage (for magic link)
      const currentPath = window.location.pathname + window.location.search;

      // Check if user is coming from QR scan - redirect to signUpLanding instead of signIn
      const isQrPath = window.location.pathname.startsWith("/qr");
      const redirectPath = isQrPath ? "/signUp" : signInPath || "/signIn";
      // We expect QR code flow to only be used in our model homes. If they get here they will never have been
      // LIKELY not have signed in before.
      // Even if they do have an account and choose to use it the redirect key being set here will get picked up
      // the next go round in this effect after logging in.
      if (currentPath !== unauthorizedUserPath) {
        localStorage.setItem(REDIRECT_KEY, currentPath);
        navigate(`${redirectPath}?redirect=${encodeURIComponent(currentPath)}`);
      } else {
        navigate(`${redirectPath}`);
      }
    } else if (requireAuth && !checkoutEnabledHomeowner) {
      navigate(unauthorizedUserPath);
    } else if (!requireAuth && isAuthenticated) {
      const storedRedirect = localStorage.getItem(REDIRECT_KEY);
      localStorage.removeItem(REDIRECT_KEY); // Clean up
      navigate(storedRedirect ?? "/"); // Navigate to stored redirect or default to landing page
    }
  }, [requireAuth, isAuthenticated, signInPath, isAuthenticating, checkoutEnabledHomeowner, navigate]);

  return <>{children}</>;
}
